import { environment } from '@/environment'
import { ComponentStatusMap, StatusResponse, statusService } from '@/services/status.service'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import packageJson from '../../../package.json'

interface SystemInfoDialogProps {
  open: boolean
  onClose: () => void
}

const infoRows = (): Array<[string, string]> => [
  ['Axiom Reports API', environment.axiomReportsApi],
  ['Sidenav Base URL', environment.sidenavBaseUrl],
  ['URL', window.location.href],
  ['User Agent', navigator.userAgent],
]

// Friendly labels for the systemInfo keys (falls back to the raw key for anything new).
const SYSTEM_INFO_LABELS: Record<string, string> = {
  AxiomAPISystemInfo: 'Axiom API',
  AxiomWorkerSystemInfo: 'Axiom Worker',
  axiomSystemInfo: 'Axiom',
  postgresSystemInfo: 'Axiom DB',
}

// Display order for the known systemInfo keys; anything new shows up after these, unordered.
const SYSTEM_INFO_ORDER = ['AxiomAPISystemInfo', 'AxiomWorkerSystemInfo', 'axiomSystemInfo', 'postgresSystemInfo']

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: 'flex', gap: 2, py: 0.75 }}>
    <Typography variant='body2' sx={{ fontWeight: 600, minWidth: 160 }}>
      {label}
    </Typography>
    <Typography variant='body2' sx={{ wordBreak: 'break-all' }}>
      {value}
    </Typography>
  </Box>
)

const ComponentList = ({ label, color, entries }: { label: string; color: string; entries: ComponentStatusMap }) => {
  const names = Object.keys(entries)
  if (names.length === 0) return null
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant='body2' sx={{ fontWeight: 600, color }}>
        {label}
      </Typography>
      {names.map((name) => (
        <Typography key={name} variant='body2' sx={{ pl: 1.5 }}>
          {name}: {JSON.stringify(entries[name])}
        </Typography>
      ))}
    </Box>
  )
}

export function SystemInfoDialog({ open, onClose }: SystemInfoDialogProps) {
  const [status, setStatus] = useState<StatusResponse | null>(null)
  const [statusError, setStatusError] = useState<string | null>(null)
  const [statusLoading, setStatusLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    setStatusLoading(true)
    setStatusError(null)
    statusService
      .getStatus()
      .then((data) => setStatus(data))
      .catch((error) => setStatusError(error?.message ?? 'Failed to load status'))
      .finally(() => setStatusLoading(false))
  }, [open])

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        System Info
        <IconButton onClick={onClose} size='small' aria-label='Close'>
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {statusLoading && <Typography variant='body2'>Loading...</Typography>}
        {statusError && (
          <Typography variant='body2' color='error'>
            {statusError}
          </Typography>
        )}
        {!statusLoading && !statusError && status && (
          <Box>
            {status.info_msg && <InfoRow label='Info' value={status.info_msg} />}
            {status.warn_msg && <InfoRow label='Warning' value={status.warn_msg} />}
            {status.critical_msg && <InfoRow label='Critical' value={status.critical_msg} />}

            <Typography variant='body2' sx={{ fontWeight: 600, mt: 1.5, mb: 0.5 }}>
              Component Versions
            </Typography>
            <InfoRow label='Axiom UI' value={packageJson.version} />
            <InfoRow label='Axiom Reporting WS' value={status.version} />
            {[
              ...SYSTEM_INFO_ORDER.filter((key) => status.systemInfo?.[key]),
              ...Object.keys(status.systemInfo ?? {}).filter((key) => !SYSTEM_INFO_ORDER.includes(key)),
            ].map((key) => {
              const info = status.systemInfo[key]
              return <InfoRow key={key} label={SYSTEM_INFO_LABELS[key] ?? key} value={info.version} />
            })}

            <Box sx={{ mt: 1.5 }}>
              <ComponentList label='Critical' color='error.main' entries={status.components?.critical ?? {}} />
              <ComponentList label='Warning' color='warning.main' entries={status.components?.warning ?? {}} />
              <ComponentList label='OK' color='success.main' entries={status.components?.ok ?? {}} />
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          {infoRows().map(([label, value]) => (
            <InfoRow key={label} label={label} value={value} />
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
